import { AuditServiceSchemaTypes } from "@bfc-software/api-schemas";
import { compareActivityEntries } from "./activity-order";
import type {
  ActivatedTask,
  ActivityEntry,
  CaptureOutcome,
  CountOutcome,
  DeviceActionEntry,
  ScanOutcome,
  WeightUom,
} from "./types";

/**
 * The task detail drawer's Activity timeline, read from audit_service's
 * `task-planner-events` instead of task-planner's `ListTaskHistory`.
 *
 * What changes is only where the entries come from. The output is the same
 * `ActivityEntry[]` that `task-activity.tsx` already turns into rows, so the
 * timeline's grouping, tones and labels are untouched.
 *
 * An event's `payload` is the producer's own event body (`tpsevents.*Event`),
 * and the spec types it as `unknown` - the service stores whatever TPS emitted
 * and an event type it has never heard of is still served. So nothing here
 * casts: each field is narrowed before it is read, and an event that does not
 * have the shape its type promises is left out of the timeline rather than
 * drawn half-filled. `rendered` is not read at all; it carries no ids.
 */

/** One item of `GET /v1/{orgExtId}/warehouses/{warehouseExtId}/task-planner-events`. */
export type WireTaskPlannerEvent =
  AuditServiceSchemaTypes.components["schemas"]["TaskPlannerEvent"];

/**
 * The `event_type` values the timeline draws, as the service stores them
 * (`tpsevents.EventType`). Every other TPS event is ignored here: selector
 * sessions and plan lifecycle belong to no task, and `TaskActivationUnassigned`,
 * `ActivationReset`, the exception and `TaskDetailResolved` events have no row
 * in the timeline today.
 */
export const TPS_EVENT_TYPE = {
  planActivated: "bfc.taskplanner.TaskPlanActivated",
  assigned: "bfc.taskplanner.TaskActivationAssigned",
  claimed: "bfc.taskplanner.TaskActivationClaimed",
  completed: "bfc.taskplanner.TaskExecutionCompleted",
  requirementEvent: "bfc.taskplanner.TaskDetailRequirementEventProcessed",
} as const;

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A non-empty string field, or null - never a number or an empty string read as text. */
function text(object: JsonObject, key: string): string | null {
  const value = object[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

const WEIGHT_UOMS: ReadonlySet<string> = new Set<WeightUom>([
  "lb",
  "oz",
  "kg",
  "g",
]);

function isWeightUom(value: string): value is WeightUom {
  return WEIGHT_UOMS.has(value);
}

function toScanOutcome(raw: unknown): ScanOutcome | null {
  if (!isObject(raw)) return null;
  switch (raw.kind) {
    case "passed": {
      const scannedValue = text(raw, "scanned_value");
      return scannedValue === null ? null : { kind: "passed", scannedValue };
    }
    case "failed": {
      const exception = text(raw, "exception");
      return exception === null ? null : { kind: "failed", exception };
    }
    default:
      return null;
  }
}

/** `lot_code_captured` and `expiration_date_captured` share their arms; only the value's key differs. */
function toCaptureOutcome(
  raw: unknown,
  valueKey: "value" | "date",
): CaptureOutcome | null {
  if (!isObject(raw)) return null;
  switch (raw.kind) {
    case "could_not_confirm":
      return { kind: "could_not_confirm" };
    case "user_entered":
    case "scanned_value": {
      const value = text(raw, valueKey);
      return value === null ? null : { kind: raw.kind, value };
    }
    default:
      return null;
  }
}

function toCountOutcome(raw: unknown): CountOutcome | null {
  if (!isObject(raw)) return null;
  switch (raw.kind) {
    case "without_exception":
      return { kind: "without_exception" };
    case "terminal_requirement":
      return { kind: "terminal_requirement" };
    case "with_exception": {
      const { actual_quantity: actualQuantity, exception } = raw;
      if (
        typeof actualQuantity !== "number" ||
        (exception !== "SOME_DAMAGED" && exception !== "NOT_ENOUGH_PRODUCT")
      ) {
        return null;
      }
      return { kind: "with_exception", actualQuantity, exception };
    }
    default:
      return null;
  }
}

/**
 * `RequirementEventProcessed.device_action` - the same union the task planner
 * served on a `TaskPickEvent.payload`, so each arm keeps its old field names.
 * Null for a kind this build does not know, which drops that one event.
 */
function toDeviceAction(raw: unknown): DeviceActionEntry | null {
  if (!isObject(raw)) return null;
  switch (raw.kind) {
    case "slot_scan":
    case "item_scan": {
      const result = toScanOutcome(raw.result);
      return result === null ? null : { kind: raw.kind, result };
    }
    case "weight_confirmation": {
      const weighedValue = text(raw, "weighed_value");
      const uom = text(raw, "unit_of_measure");
      return weighedValue === null || uom === null || !isWeightUom(uom)
        ? null
        : { kind: "weight_confirmation", weighedValue, uom };
    }
    case "lot_code_capture": {
      const captured = toCaptureOutcome(raw.lot_code_captured, "value");
      return captured === null ? null : { kind: "lot_code_capture", captured };
    }
    case "expiration_date_capture": {
      const captured = toCaptureOutcome(raw.expiration_date_captured, "date");
      return captured === null
        ? null
        : { kind: "expiration_date_capture", captured };
    }
    case "count_confirmation": {
      const result = toCountOutcome(raw.result);
      return result === null ? null : { kind: "count_confirmation", result };
    }
    default:
      return null;
  }
}

/**
 * The reassignment an Assigned event records, or null for any other trigger.
 * `assignment_trigger` is the producer's `manager_assigned` arm
 * (`tps_events.yaml`): its `assignment` names the previous holder, and its
 * `authority` the manager (a user for `api_user`; API-key auth has none).
 */
function reassignmentOf(
  body: JsonObject,
): Extract<ActivityEntry, { kind: "assigned" }>["reassignment"] {
  const trigger = body.assignment_trigger;
  if (!isObject(trigger) || trigger.trigger !== "manager_assigned") return null;
  const assignment = trigger.assignment;
  if (!isObject(assignment) || assignment.kind !== "reassign_selector") {
    return null;
  }
  const authority = isObject(trigger.authority) ? trigger.authority : null;
  return {
    fromSelectorId: text(assignment, "previous_selector_id"),
    byUser:
      authority !== null && authority.authority === "api_user"
        ? text(authority, "external_user_id")
        : null,
  };
}

/** The event's body, or null when `payload` is not a JSON object. */
function bodyOf(event: WireTaskPlannerEvent): JsonObject | null {
  return isObject(event.payload) ? event.payload : null;
}

/**
 * One of the task's own events -> its timeline entry, or null for a type the
 * timeline does not draw or a body that is missing what the entry needs.
 *
 * Times are the body's own (`assigned_at`, `claimed_at`, `completed_at`,
 * `occurred_at`) rather than the row's `received_at`: those are when the thing
 * happened, which is what the old feed reported, where `received_at` is when
 * audit_service got to hear of it.
 */
function toTaskEntry(
  event: WireTaskPlannerEvent,
  taskActivationId: string,
): ActivityEntry | null {
  const body = bodyOf(event);
  if (body === null) return null;

  switch (event.event_type) {
    case TPS_EVENT_TYPE.assigned: {
      const timestamp = text(body, "assigned_at");
      return timestamp === null
        ? null
        : {
            kind: "assigned",
            taskActivationId,
            timestamp,
            selectorId: text(body, "selector_id"),
            reassignment: reassignmentOf(body),
          };
    }
    case TPS_EVENT_TYPE.claimed: {
      const timestamp = text(body, "claimed_at");
      return timestamp === null
        ? null
        : {
            kind: "started",
            taskActivationId,
            timestamp,
            selectorId: text(body, "selector_id"),
          };
    }
    case TPS_EVENT_TYPE.completed: {
      const timestamp = text(body, "completed_at");
      return timestamp === null
        ? null
        : {
            kind: "completed",
            taskActivationId,
            timestamp,
            selectorId: text(body, "selector_id"),
          };
    }
    case TPS_EVENT_TYPE.requirementEvent: {
      const action = toDeviceAction(body.device_action);
      const pickId = text(body, "task_detail_pick_id");
      const selectorId = text(body, "selector_id");
      const occurredAt = text(body, "occurred_at");
      if (
        action === null ||
        pickId === null ||
        selectorId === null ||
        occurredAt === null
      ) {
        return null;
      }
      return {
        kind: "pick",
        eventId: text(body, "requirement_event_id") ?? event.event_id,
        pickId,
        selectorId,
        occurredAt,
        processedAt: text(body, "processed_at"),
        action,
      };
    }
    default:
      return null;
  }
}

/**
 * The task's `activated` entry, from its plan's `TaskPlanActivated` event.
 *
 * TPS has no per-task activation event, so this is the plan's. A plan can be
 * activated more than once - a reset and a re-release - and each activation
 * emits its own, so the match is on `plan_activation_id`: the plan alone would
 * hand every task the time of whichever activation was newest. Null when none
 * of `planEvents` is this task's activation.
 */
function toActivatedEntry(
  planEvents: readonly WireTaskPlannerEvent[],
  task: Pick<ActivatedTask, "activationId" | "planActivationId">,
): ActivityEntry | null {
  for (const event of planEvents) {
    if (event.event_type !== TPS_EVENT_TYPE.planActivated) continue;
    const body = bodyOf(event);
    if (body === null) continue;
    if (text(body, "plan_activation_id") !== task.planActivationId) continue;
    const timestamp = text(body, "activated_at");
    if (timestamp !== null) {
      return {
        kind: "activated",
        taskActivationId: task.activationId,
        timestamp,
      };
    }
  }
  return null;
}

/**
 * A task's Activity entries, oldest first, in the order `compareActivityEntries`
 * defines.
 *
 * `taskEvents` is every event filtered to the task's `task_activation_id`;
 * `planEvents` is the plan's `TaskPlanActivated` events. Neither list's order
 * is relied on - the service's is not guaranteed to be chronological in either
 * direction - so the order comes from the entries' own times alone.
 */
export function toActivityEntriesFromEvents(
  task: Pick<ActivatedTask, "activationId" | "planActivationId">,
  taskEvents: readonly WireTaskPlannerEvent[],
  planEvents: readonly WireTaskPlannerEvent[],
): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  const activated = toActivatedEntry(planEvents, task);
  if (activated !== null) entries.push(activated);

  for (const event of taskEvents) {
    const entry = toTaskEntry(event, task.activationId);
    if (entry !== null) entries.push(entry);
  }

  return entries.sort(compareActivityEntries);
}
